import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tag, Plus, Pencil, Trash2, X, Image as ImageIcon } from 'lucide-react';
import {
  fetchPromotions,
  createPromotion,
  updatePromotion,
  deletePromotion,
  PROMO_SLOTS,
} from '../api/promotionsApi';
import { getImageUrl } from '../api/imageUtils';

const EMPTY_FORM = {
  slot: 'row1_tiles',
  sortOrder: 0,
  tag: '',
  heading: '',
  headingLine2: '',
  discountText: '',
  subtitle: '',
  bodyText: '',
  ctaPrimaryLabel: 'Shop Now',
  ctaPrimaryUrl: '/products',
  ctaSecondaryLabel: '',
  ctaSecondaryUrl: '',
  categorySlug: '',
  panelStyle: 'moss',
  isActive: true,
};

const Promotions = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterSlot, setFilterSlot] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const imagePreviewRef = useRef('');

  const resetImageState = () => {
    if (imagePreviewRef.current) {
      URL.revokeObjectURL(imagePreviewRef.current);
      imagePreviewRef.current = '';
    }
    setFile(null);
    setImagePreview('');
    setRemoveExistingImage(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => () => {
    if (imagePreviewRef.current) URL.revokeObjectURL(imagePreviewRef.current);
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const list = await fetchPromotions();
      setItems(list);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || e.message || 'Failed to load promotions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (filterSlot === 'all') return items;
    return items.filter((i) => i.slot === filterSlot);
  }, [items, filterSlot]);

  const slotLabel = (slot) => PROMO_SLOTS.find((s) => s.value === slot)?.label || slot;

  const openCreate = () => {
    setEditing(null);
    resetImageState();
    setForm({ ...EMPTY_FORM, slot: filterSlot === 'all' ? 'row1_tiles' : filterSlot });
    setModalOpen(true);
    setError('');
  };

  const openEdit = (item) => {
    resetImageState();
    setEditing(item);
    setForm({
      slot: item.slot,
      sortOrder: item.sortOrder,
      tag: item.tag || '',
      heading: item.heading || '',
      headingLine2: item.headingLine2 || '',
      discountText: item.discountText || '',
      subtitle: item.subtitle || '',
      bodyText: item.bodyText || '',
      ctaPrimaryLabel: item.ctaPrimaryLabel || 'Shop Now',
      ctaPrimaryUrl: item.ctaPrimaryUrl || '/products',
      ctaSecondaryLabel: item.ctaSecondaryLabel || '',
      ctaSecondaryUrl: item.ctaSecondaryUrl || '',
      categorySlug: item.categorySlug || '',
      panelStyle: item.panelStyle || 'moss',
      isActive: item.isActive,
    });
    setModalOpen(true);
    setError('');
  };

  const closeModal = () => {
    resetImageState();
    setModalOpen(false);
    setEditing(null);
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0] || null;
    if (!selected) return;

    if (imagePreviewRef.current) URL.revokeObjectURL(imagePreviewRef.current);
    const previewUrl = URL.createObjectURL(selected);
    imagePreviewRef.current = previewUrl;
    setFile(selected);
    setImagePreview(previewUrl);
    setRemoveExistingImage(false);
    e.target.value = '';
  };

  const handleRemoveImage = () => {
    if (imagePreviewRef.current) {
      URL.revokeObjectURL(imagePreviewRef.current);
      imagePreviewRef.current = '';
    }
    setFile(null);
    setImagePreview('');
    if (editing?.imagePath) setRemoveExistingImage(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const displayImage = imagePreview
    || (!removeExistingImage && editing?.imagePath ? getImageUrl(editing.imagePath) : '');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const buildPayload = () => ({
    slot: form.slot,
    sort_order: String(form.sortOrder ?? 0),
    tag: form.tag,
    heading: form.heading,
    heading_line2: form.headingLine2,
    discount_text: form.discountText,
    subtitle: form.subtitle,
    body_text: form.bodyText,
    cta_primary_label: form.ctaPrimaryLabel,
    cta_primary_url: form.ctaPrimaryUrl,
    cta_secondary_label: form.ctaSecondaryLabel,
    cta_secondary_url: form.ctaSecondaryUrl,
    category_slug: form.categorySlug,
    panel_style: form.panelStyle,
    is_active: String(Boolean(form.isActive)),
    file,
  });

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = buildPayload();
      if (editing) {
        await updatePromotion(editing.id, payload);
      } else {
        await createPromotion(payload);
      }
      closeModal();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promotion item?')) return;
    try {
      await deletePromotion(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Delete failed');
    }
  };

  const isTile = form.slot.includes('tiles');

  return (
    <>
    <div className="ui-page animate-fade-in">
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight text-gradient">Deals & Promotions</h1>
          <p className="text-slate-500 mt-1 font-medium text-sm">
            Manage the homepage &quot;Deals & Promotions&quot; section — panels, tiles, and banner text.
          </p>
        </div>
        <button type="button" onClick={openCreate} className="ui-btn ui-btn-primary inline-flex items-center gap-2">
          <Plus size={16} /> Add item
        </button>
      </div>

      <div className="ui-card ui-card-pad mb-6 flex flex-wrap gap-3 items-center">
        <label className="text-sm font-semibold text-slate-600">Filter by slot:</label>
        <select
          value={filterSlot}
          onChange={(e) => setFilterSlot(e.target.value)}
          className="ui-input max-w-xs"
        >
          <option value="all">All slots</option>
          {PROMO_SLOTS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {error && !modalOpen && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
      )}

      {loading ? (
        <p className="text-slate-500">Loading…</p>
      ) : (
        <div className="grid gap-4">
          {filtered.map((item) => (
            <div key={item.id} className="ui-card ui-card-pad flex flex-col md:flex-row gap-4 md:items-center">
              <div className="w-20 h-20 rounded-lg bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                {item.imagePath ? (
                  <img src={getImageUrl(item.imagePath)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="text-slate-300" size={28} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                    {slotLabel(item.slot)}
                  </span>
                  <span className="text-xs text-slate-400">Order: {item.sortOrder}</span>
                  {!item.isActive && (
                    <span className="text-xs font-semibold text-rose-600 bg-rose-50 px-2 py-0.5 rounded">Inactive</span>
                  )}
                </div>
                <h3 className="font-bold text-slate-800 truncate">{item.heading || item.tag || '—'}</h3>
                <p className="text-sm text-slate-500 truncate">
                  {item.discountText || item.subtitle || item.bodyText || item.categorySlug || '—'}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button type="button" onClick={() => openEdit(item)} className="ui-btn ui-btn-outline inline-flex items-center gap-1">
                  <Pencil size={14} /> Edit
                </button>
                <button type="button" onClick={() => handleDelete(item.id)} className="ui-btn ui-btn-danger inline-flex items-center gap-1">
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
          {!filtered.length && (
            <p className="text-slate-500 text-center py-8">No promotion items yet. Run the SQL migration or add items.</p>
          )}
        </div>
      )}
    </div>

      {modalOpen && (
        <div className="ui-modal-overlay">
          <div className="ui-modal-panel max-w-2xl overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-gradient-to-r from-emerald-50/60 to-green-50/60">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Tag size={18} className="text-emerald-600" />
                {editing ? 'Edit promotion' : 'Add promotion'}
              </h2>
              <button type="button" onClick={closeModal} className="p-2 rounded-lg hover:bg-slate-100 border border-slate-200 bg-white">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-5 space-y-4">
              {error && (
                <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="ui-label">Slot *</label>
                  <select name="slot" value={form.slot} onChange={handleChange} className="ui-input w-full" required>
                    {PROMO_SLOTS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="ui-label">Sort order</label>
                  <input type="number" name="sortOrder" min="0" value={form.sortOrder} onChange={handleChange} className="ui-input w-full" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="ui-label">Tag / label</label>
                  <input name="tag" value={form.tag} onChange={handleChange} className="ui-input w-full" placeholder="Healthy Organic" />
                </div>
                <div>
                  <label className="ui-label">Panel style</label>
                  <select name="panelStyle" value={form.panelStyle} onChange={handleChange} className="ui-input w-full">
                    <option value="moss">Moss (green)</option>
                    <option value="dark">Dark</option>
                    <option value="banner">Banner (full width)</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="ui-label">Heading *</label>
                  <input name="heading" value={form.heading} onChange={handleChange} className="ui-input w-full" required />
                </div>
                <div>
                  <label className="ui-label">Heading line 2</label>
                  <input name="headingLine2" value={form.headingLine2} onChange={handleChange} className="ui-input w-full" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="ui-label">Discount text</label>
                  <input name="discountText" value={form.discountText} onChange={handleChange} className="ui-input w-full" placeholder="50% FLAT OFFER" />
                </div>
                <div>
                  <label className="ui-label">Subtitle</label>
                  <input name="subtitle" value={form.subtitle} onChange={handleChange} className="ui-input w-full" />
                </div>
              </div>

              <div>
                <label className="ui-label">Body text</label>
                <textarea name="bodyText" value={form.bodyText} onChange={handleChange} rows={2} className="ui-input w-full" />
              </div>

              {isTile && (
                <div>
                  <label className="ui-label">Category slug (for tile link)</label>
                  <input name="categorySlug" value={form.categorySlug} onChange={handleChange} className="ui-input w-full" placeholder="spices-masala" />
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="ui-label">Primary CTA label</label>
                  <input name="ctaPrimaryLabel" value={form.ctaPrimaryLabel} onChange={handleChange} className="ui-input w-full" />
                </div>
                <div>
                  <label className="ui-label">Primary CTA URL</label>
                  <input name="ctaPrimaryUrl" value={form.ctaPrimaryUrl} onChange={handleChange} className="ui-input w-full" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="ui-label">Secondary CTA label</label>
                  <input name="ctaSecondaryLabel" value={form.ctaSecondaryLabel} onChange={handleChange} className="ui-input w-full" />
                </div>
                <div>
                  <label className="ui-label">Secondary CTA URL</label>
                  <input name="ctaSecondaryUrl" value={form.ctaSecondaryUrl} onChange={handleChange} className="ui-input w-full" />
                </div>
              </div>

              <div>
                <label className="ui-label">Background / tile image</label>
                {displayImage ? (
                  <div className="relative mt-1">
                    <img
                      src={displayImage}
                      alt={form.heading || 'Promotion'}
                      className="w-full max-h-48 rounded-xl border border-slate-200 object-cover"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/90 border border-slate-200 text-slate-600 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition-colors shadow-sm"
                      aria-label="Remove image"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="ui-input w-full mt-1"
                  />
                )}
              </div>

              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} />
                Active (visible on website)
              </label>

              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className="ui-btn ui-btn-primary">
                  {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
                </button>
                <button type="button" onClick={closeModal} className="ui-btn ui-btn-outline">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Promotions;
